import { DatePipe } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, inject, input, output, signal } from '@angular/core';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';

import { PlanningCandidateKind, PlanningCandidateModel, PlanningCandidatesService, isBindable, isBound } from '@lib/guarantees/data-access';
import { TranslatePipe } from '@ngx-translate/core';
import { catchError, combineLatest, of, switchMap, tap } from 'rxjs';

import { MatIcon } from '@angular/material/icon';

import { CalloutComponent } from '../components/common/callout.component';
import { SpinnerComponent } from '../components/common/spinner.component';

/** One offered record with the answer already worked out, so the row itself decides nothing. */
interface CandidateRow {
  candidate: PlanningCandidateModel;
  /** Why this one cannot be taken, or empty where it can. */
  refusalKey: string;
  selected: boolean;
  className: string;
}

const ROW_BASE =
  'flex w-full flex-col gap-1 rounded-lg border px-2.5 py-2 text-left transition-colors duration-150 focus-visible:ring-2 focus-visible:ring-primary-500/40 focus-visible:outline-none motion-reduce:transition-none';
const ROW_OPEN = `${ROW_BASE} border-neutral-200 bg-neutral-0 hover:bg-neutral-50`;
const ROW_SELECTED = `${ROW_BASE} border-primary-300 bg-primary-50`;
const ROW_REFUSED = `${ROW_BASE} cursor-not-allowed border-neutral-100 bg-neutral-50`;

function refusalKeyOf(candidate: PlanningCandidateModel): string {
  if (isBound(candidate)) {
    return 'guaranteeV2.message.candidateAlreadyBound';
  }

  return isBindable(candidate) ? '' : 'guaranteeV2.message.candidateNotOpen';
}

/**
 * The bookings the member already holds, to deliver these lines by one of them.
 *
 * No partner is named first: these are records of ours, so there is one list of them rather than
 * one list per partner to be asked in turn. Taking one answers the whole of where, who and when in
 * a single act, which is the reason to point at a booking rather than describe it again by hand.
 *
 * Everything still worth offering is shown, including what cannot be taken: a visit already past or
 * an order already collected is the reason the list looks short, and hiding it only makes the flow
 * look broken. The reason stands in the row as text rather than behind a hover — a row that refuses
 * to be clicked refuses to be hovered as well. A record another line is already delivered by is
 * refused the same way: one booking answers the lines it was bound with, and a second binding would
 * be a second arrangement.
 */
@Component({
  selector: 'guarantees-candidate-picker',
  standalone: true,
  imports: [DatePipe, MatIcon, TranslatePipe, CalloutComponent, SpinnerComponent],
  templateUrl: './candidate-picker.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CandidatePickerComponent {
  memberId = input.required<string>();
  kind = input.required<PlanningCandidateKind>();
  /** What was taken before, so stepping back and forward again shows the answer rather than blanks. */
  selected = input<PlanningCandidateModel | undefined>(undefined);

  chosen = output<PlanningCandidateModel>();

  private readonly candidates = inject(PlanningCandidatesService);

  /** Bumped to ask again after a read that failed — nothing about the question has changed. */
  private readonly attempt = signal(0);

  protected readonly loading = signal(true);
  protected readonly failed = signal(false);

  private readonly offered = signal<PlanningCandidateModel[]>([]);

  protected readonly rows = computed<CandidateRow[]>(() => {
    const selectedId = this.selected()?.id;

    return this.offered().map(candidate => {
      const refusalKey = refusalKeyOf(candidate);
      const selected = candidate.id === selectedId;

      return { candidate, refusalKey, selected, className: refusalKey ? ROW_REFUSED : selected ? ROW_SELECTED : ROW_OPEN };
    });
  });

  protected readonly empty = computed(() => !this.loading() && !this.failed() && this.rows().length === 0);

  /** What the list is for: how many of these could actually be taken, not how many exist. */
  protected readonly bindableCount = computed(() => this.rows().filter(row => !row.refusalKey).length);

  constructor() {
    combineLatest([toObservable(this.memberId), toObservable(this.kind), toObservable(this.attempt)])
      .pipe(
        tap(() => {
          this.loading.set(true);
          this.failed.set(false);
          this.offered.set([]);
        }),
        switchMap(([memberId, kind]) =>
          this.candidates.search(memberId, kind).pipe(
            catchError(() => {
              this.failed.set(true);

              return of<PlanningCandidateModel[]>([]);
            })
          )
        ),
        takeUntilDestroyed()
      )
      .subscribe(offered => {
        this.offered.set(offered);
        this.loading.set(false);
      });
  }

  protected choose(row: CandidateRow): void {
    if (row.refusalKey) {
      return;
    }

    this.chosen.emit(row.candidate);
  }

  protected retry(): void {
    this.attempt.update(count => count + 1);
  }
}
